import React from 'react'
import { ResourcePlanSchema } from '../data/types'
import { planRecurrenceMap, styleThousandCommas } from '../utils/helpers'

type FundingRatePlansProps = {
  plans?: ResourcePlanSchema[]
  trialDays?: number
}

/**
 * Scaled-down rate list for a funding listing. A funding offer prices a recurring
 * access fee, so each rate shows only its price, cadence, and what the fee includes.
 */
export const FundingRatePlans: React.FC<FundingRatePlansProps> = ({ plans, trialDays }) => {
  const rates = (plans || []).filter((plan) => Number(plan?.plan_rate) > 0)
  if (rates.length === 0) return null

  return (
    <div className='mt-3 flex flex-col gap-3' data-testid='funding-rate-plans'>
      {rates.map((plan, index) => (
        <div key={plan.plan_id || index} className='rounded-xl bg-white p-3'>
          <div className='flex flex-wrap items-baseline gap-x-2'>
            <p className='font-raleway text-[20px] font-semibold text-black'>
              ${styleThousandCommas(plan.plan_rate || 0)}
              <span className='ml-1 text-sm font-medium text-black/70'>
                / {planRecurrenceMap[plan.plan_recurrence as keyof typeof planRecurrenceMap]}
              </span>
            </p>
            {trialDays ? (
              <span className='rounded-full bg-primary-tint px-2.5 py-1 text-xs text-primaryDark-0'>
                {trialDays}-day free trial
              </span>
            ) : null}
          </div>
          {plan.plan_description && (
            <p className='mt-1 font-open-sans text-sm text-gray-700'>{plan.plan_description}</p>
          )}
          {plan.overage_rate ? (
            <p className='mt-1 font-open-sans text-xs text-gray-500'>
              Overage ${styleThousandCommas(plan.overage_rate)}
            </p>
          ) : null}
          {(plan.features && plan.features.length > 0) && (
            <ul className='mt-2 list-disc pl-5 font-open-sans text-sm text-gray-700'>
              {plan.features.map((feature: string, featureIndex: number) => (
                <li key={featureIndex}>{feature}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  )
}

export default FundingRatePlans
